// ============================================================
// xFreeScan - Upgrade Prompt Component
// Shows upgrade CTAs in different variants: inline, modal, overlay
// ============================================================
import React, { useState } from 'react';
import { Lock, Zap, Shield, ArrowRight, X } from 'lucide-react';

interface UpgradePromptProps {
  variant: 'inline' | 'modal' | 'overlay';
  feature?: string;
  additionalCount?: number;
  onClose?: () => void;
  benefits?: string[];
}

export function UpgradePrompt({ variant, feature, additionalCount, onClose, benefits }: UpgradePromptProps) {
  if (variant === 'inline') {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-lg">
        <Lock className="w-4 h-4 text-amber-500 flex-shrink-0" />
        <span className="text-sm text-amber-200">
          {feature || 'Esta funcionalidad'} requiere <strong>Plan Pro</strong>
        </span>
        <a href="/pricing" className="ml-auto text-sm font-semibold text-amber-400 hover:text-amber-300 flex items-center gap-1">
          Upgrade <ArrowRight className="w-3 h-3" />
        </a>
      </div>
    );
  }

  if (variant === 'overlay') {
    return (
      <div className="relative">
        <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-sm rounded-lg flex flex-col items-center justify-center z-10 p-6">
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-3 rounded-full mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">
            {additionalCount
              ? `${additionalCount} hallazgos adicionales`
              : 'Contenido Pro'}
          </h3>
          <p className="text-sm text-gray-300 text-center mb-4 max-w-xs">
            {additionalCount
              ? `Se encontraron ${additionalCount} vulnerabilidades adicionales con modulos avanzados. Actualiza a Pro para verlas.`
              : 'Esta seccion incluye analisis detallado, evidencia y recomendaciones ejecutivas.'}
          </p>
          <a
            href="/pricing"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all"
          >
            <Zap className="w-4 h-4" /> Ver Planes Pro
          </a>
        </div>
      </div>
    );
  }

  // Modal variant
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-2xl max-w-lg w-full p-8 relative border border-gray-700">
        {onClose && (
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        )}

        <div className="text-center mb-6">
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 p-4 rounded-full inline-block mb-4">
            <Zap className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Actualiza a Pro</h2>
          <p className="text-gray-400">
            Desbloquea el poder completo de xFreeScan con pentesting automatizado
          </p>
        </div>

        <div className="space-y-3 mb-6">
          {(benefits || [
            '26 modulos de escaneo (vs 9 en Free)',
            '12 modulos de pentesting activo automatizado',
            'Reporte ejecutivo completo con evidencia PoC',
            '20 escaneos diarios (vs 3)',
            'Export PDF para presentar a directivos',
            'Brute force, CSRF, clickjacking, SQLi, XSS y mas',
          ]).map((benefit, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-3 h-3 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-sm text-gray-300">{benefit}</span>
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <a
            href="/pricing"
            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all"
          >
            <Zap className="w-4 h-4" /> Ver Planes
          </a>
          {onClose && (
            <button
              onClick={onClose}
              className="px-6 py-3 text-gray-400 hover:text-white border border-gray-600 rounded-lg transition-colors"
            >
              Ahora no
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Badge component for plan indicator
export function PlanBadge({ plan }: { plan: string }) {
  if (plan === 'pro') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-semibold rounded-full border border-amber-500/30">
        <Zap className="w-3 h-3" /> PRO
      </span>
    );
  }
  if (plan === 'enterprise') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs font-semibold rounded-full border border-purple-500/30">
        <Shield className="w-3 h-3" /> ENTERPRISE
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-500/20 text-gray-400 text-xs font-semibold rounded-full border border-gray-500/30">
      FREE
    </span>
  );
}

// Lock overlay for disabled modules
export function ModuleLock({ tier }: { tier: 'deep' | 'active' }) {
  return (
    <div className="absolute inset-0 bg-gray-900/70 backdrop-blur-[2px] rounded-lg flex flex-col items-center justify-center cursor-pointer group">
      <Lock className="w-5 h-5 text-amber-500 mb-1 group-hover:scale-110 transition-transform" />
      <span className="text-xs text-amber-400 font-medium">
        {tier === 'active' ? 'Pro — Pentest' : 'Pro'}
      </span>
    </div>
  );
}
